/**
 * Realtime channel scoping helper (Fix #1).
 *
 * Boot flag `VITE_REALTIME_SCOPING` controls whether channels are created with
 * the new scoped topic + `private: true` flag, or fall back to today's
 * unscoped behavior. Default is OFF so this file ships dark.
 *
 * Usage:
 *   const ch = scopedChannel({ kind: 'booking', id: bookingId }, 'updates');
 *   ch.on('postgres_changes', { event: '*', schema: 'public', table: 'bookings', filter: `id=eq.${bookingId}` }, ...).subscribe();
 *
 * The matching DB-side authorization function `public.realtime_can_subscribe(_topic)`
 * gates `realtime.messages` access for `private: true` channels. Until the
 * server-side `platform_settings.realtime_scoping_enabled` flag is `'on'`, the
 * function always returns true — preserving today's behavior.
 */
import { supabase } from "@/integrations/supabase/client";

/**
 * Per-tab channel cardinality metric.
 *
 * Tracks how many scoped channels are currently subscribed in this tab,
 * the peak observed, and how many reconnects (CHANNEL_ERROR / TIMED_OUT
 * followed by re-SUBSCRIBE) we've seen. Exposed on `window.__rtMetrics`
 * for ad-hoc inspection during the migration; safe to leave shipped.
 */
type RtMetrics = {
  active: number;
  peak: number;
  totalSubscribed: number;
  reconnects: number;
  byKind: Record<string, number>;
  snapshot: () => Pick<RtMetrics, "active" | "peak" | "totalSubscribed" | "reconnects" | "byKind">;
};

function getMetrics(): RtMetrics {
  const w = (typeof window !== "undefined" ? window : {}) as unknown as {
    __rtMetrics?: RtMetrics;
  };
  if (!w.__rtMetrics) {
    const m: RtMetrics = {
      active: 0,
      peak: 0,
      totalSubscribed: 0,
      reconnects: 0,
      byKind: {},
      snapshot() {
        return {
          active: m.active,
          peak: m.peak,
          totalSubscribed: m.totalSubscribed,
          reconnects: m.reconnects,
          byKind: { ...m.byKind },
        };
      },
    };
    w.__rtMetrics = m;
  }
  return w.__rtMetrics;
}

export function readRealtimeMetrics() {
  return getMetrics().snapshot();
}

/**
 * Channel scope kinds.
 *
 * Expected topic shapes (parser contract — must match `realtime_can_subscribe`):
 *   user:{uuid}[:suffix]            e.g. user:abc:notifications
 *   booking:{uuid}[:suffix]         e.g. booking:abc:detail
 *   trip:{uuid}[:suffix]            e.g. trip:abc:updates
 *   conversation:{uuid}[:suffix]    e.g. conversation:abc:messages
 *   ticket:{uuid}[:suffix]          e.g. ticket:abc:messages
 *   affiliate:{uuid}[:suffix]       e.g. affiliate:abc:cashouts
 *   public:{slug}[:suffix]          e.g. public:health:ping (no auth gate, intentionally broad)
 *   role:{role}[:suffix]            e.g. role:courier:pending-bookings (gated by has_role)
 */
export type RealtimeScope =
  | { kind: "user" | "booking" | "trip" | "conversation" | "ticket" | "affiliate"; id: string }
  | { kind: "role"; id: RoleId }
  | { kind: "public"; id: string };

/**
 * Closed set of allowed role topics. Keep in sync with the DB-side
 * `realtime_can_subscribe` parser. Adding a new role here without updating
 * the parser will silently fail-open while the flag is OFF and silently
 * fail-closed once the flag flips ON — so this list is the contract.
 */
export type RoleId = "courier" | "staff" | "admin" | "support" | "customer";
const ALLOWED_ROLES: ReadonlySet<RoleId> = new Set([
  "courier",
  "staff",
  "admin",
  "support",
  "customer",
]);

/**
 * Marker helper for Category-B call sites that subscribe to a shared
 * pool feed gated by a server-side role check (e.g. courier match queue,
 * staff escalation badge). Auditors can grep for `roleScope(` to
 * distinguish role pools from per-user listeners.
 */
export function roleScope(role: RoleId): RealtimeScope {
  if (!ALLOWED_ROLES.has(role)) {
    throw new Error(`[realtime] roleScope called with unknown role "${role}"`);
  }
  return { kind: "role", id: role };
}

/**
 * Marker helper for Category-C call sites that intentionally stay broad
 * (system health, dev tooling, "all" feeds). Auditors can grep for
 * `publicScope(` to confirm intent rather than treating it as a missed
 * migration.
 */
export function publicScope(slug: string): RealtimeScope {
  return { kind: "public", id: slug };
}

/** Allowed suffix shape: lowercase alphanumerics + dashes only. No colons. */
const SAFE_SUFFIX_RE = /^[a-z0-9-]+$/;

/**
 * Reject suffixes that could break namespace guarantees (colons, dots, slashes,
 * uppercase, empty strings). Throws — callers must pass static, code-defined
 * suffixes, never user input.
 */
export function assertSafeSuffix(suffix: string): void {
  if (!SAFE_SUFFIX_RE.test(suffix)) {
    throw new Error(
      `[realtime] unsafe channel suffix "${suffix}" — must match ${SAFE_SUFFIX_RE}`,
    );
  }
}

/** Read the client-side kill switch. Default: off (legacy behavior). */
export function isRealtimeScopingEnabled(): boolean {
  // Vite inlines `import.meta.env.*` at build time.
  const flag = (import.meta as { env?: Record<string, string | undefined> }).env
    ?.VITE_REALTIME_SCOPING;
  return flag === "on";
}

/**
 * Build a deterministic topic string for a scope.
 * Format: `<kind>:<uuid>[:<suffix>]` — must match the parser inside
 * `public.realtime_can_subscribe`.
 */
export function scopedTopic(scope: RealtimeScope, suffix?: string): string {
  if (suffix !== undefined) assertSafeSuffix(suffix);
  const base = `${scope.kind}:${scope.id}`;
  return suffix ? `${base}:${suffix}` : base;
}

/**
 * Create a Supabase Realtime channel scoped to a single
 * user/booking/trip/conversation/ticket/affiliate/role.
 *
 * Topic format: `<kind>:<id>[:<suffix>]` (see `scopedTopic`). Subscribed with
 * `config.private = true`, which routes broadcast/presence through
 * `realtime.messages` and triggers the `realtime_can_subscribe` DB policy.
 * `public:*` topics are an explicit exception — they stay non-private so
 * unauthenticated/broad listeners (health pings, dev tooling) keep working.
 *
 * The `isRealtimeScopingEnabled()` boot flag is preserved as a runtime kill
 * switch: if `VITE_REALTIME_SCOPING` is anything other than `"on"`, channels
 * fall back to non-private mode so a single redeploy can revert behavior.
 * Scheduled for removal one release after Phase 5 lands.
 */
export function scopedChannel(scope: RealtimeScope, suffix?: string) {
  const enabled = isRealtimeScopingEnabled();
  // Hard guard: refuse empty/undefined IDs that would produce poisoned topics
  // like `booking:undefined`. Surfacing this loudly is far cheaper than
  // debugging silent listener drift.
  if (!scope.id || scope.id === "undefined" || scope.id === "null") {
    throw new Error(
      `[realtime] scopedChannel called with invalid scope id for kind="${scope.kind}"`,
    );
  }
  const topic = scopedTopic(scope, suffix);
  removeExistingChannelForTopic(topic);
  const channel = supabase.channel(topic, {
    config: enabled && scope.kind !== "public" ? { private: true } : {},
  });
  return instrument(channel, scope.kind);
}

function removeExistingChannelForTopic(topic: string): void {
  const realtimeTopic = `realtime:${topic}`;
  const channels = supabase.getChannels().filter((channel) => channel.topic === realtimeTopic);

  for (const channel of channels) {
    void supabase.removeChannel(channel);
    (supabase.realtime as unknown as { _remove?: (channelToRemove: unknown) => void })._remove?.(channel);
  }
}

/**
 * Wrap a channel's `subscribe` to feed the cardinality metric.
 * - SUBSCRIBED   -> active++ (or reconnect++ if we'd already counted it)
 * - CLOSED       -> active--
 * - CHANNEL_ERROR/TIMED_OUT before next SUBSCRIBED counts as a reconnect.
 */
function instrument<T extends { subscribe: (cb?: (status: string) => void) => unknown }>(
  channel: T,
  kind: string,
): T {
  const m = getMetrics();
  const original = channel.subscribe.bind(channel);
  let counted = false;
  let sawError = false;
  (channel as { subscribe: (cb?: (status: string) => void) => unknown }).subscribe = (
    cb?: (status: string) => void,
  ) =>
    original((status: string) => {
      if (status === "SUBSCRIBED") {
        if (!counted) {
          counted = true;
          m.active += 1;
          m.totalSubscribed += 1;
          m.byKind[kind] = (m.byKind[kind] ?? 0) + 1;
          if (m.active > m.peak) m.peak = m.active;
        } else if (sawError) {
          m.reconnects += 1;
        }
        sawError = false;
      } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
        sawError = true;
      } else if (status === "CLOSED") {
        if (counted) {
          counted = false;
          m.active = Math.max(0, m.active - 1);
        }
      }
      cb?.(status);
    });
  return channel;
}